/**
 * Persistent account-state banner.
 *
 * Shown when Firebase authenticated the user but `POST /auth/sync` failed, so
 * there is no server-side profile. That is not a single failed request — it
 * breaks favourites, history, playlists and settings simultaneously — so it
 * gets a banner that stays until it is resolved rather than a toast that
 * disappears after four seconds.
 */

import { useAuth } from '../../providers/AuthProvider.jsx';
import { Button } from '../ui/Button.jsx';
import { describeSyncError } from './syncErrorCopy.js';
import styles from './AccountBanner.module.css';

export function AccountBanner() {
  const { profileMissing, syncError, retryingSync, retrySync, signOut } = useAuth();

  if (!profileMissing) return null;

  const { isAuthProblem, message, detail } = describeSyncError(syncError);

  return (
    <div className={styles.banner} role="alert">
      <div className={styles.text}>
        <strong className={styles.title}>Your library did not load</strong>
        <span className={styles.message}>{message}</span>
        {detail && <code className={styles.detail}>{detail}</code>}
      </div>

      <div className={styles.actions}>
        <Button variant="primary" size="sm" loading={retryingSync} onClick={retrySync}>
          Retry
        </Button>

        {isAuthProblem && (
          <Button variant="ghost" size="sm" onClick={signOut}>
            Sign in again
          </Button>
        )}
      </div>
    </div>
  );
}
